/**
 * The ad library: one curated list, filtered two ways.
 *
 * - Home shows only `home: true` entries: the strongest software ads (UI-only
 *   spots for Arceus and JK) and a few generated scenes.
 * - /marketing shows `library: true` entries grouped by category. It is a
 *   selection, not an archive: a spot is added here once, when it is finished
 *   and the founders have signed it off.
 *
 * Files live in /public/ads (new encodes, 2026-10-30) and /public/reel (older
 * spots kept as they were). Every clip is H.264, faststart, lazily loaded by
 * ReelVideo, and plays muted; `audio` only marks that a voice-over exists.
 */
export type AdCategory = 'software' | 'ai-video' | 'fight' | 'car';

/** 'phone' is 9:16, 'landscape' is 16:9, 'scene' is the 768x448 LTX frame. */
export type AdLayout = 'phone' | 'landscape' | 'scene';

export interface Ad {
  id: string;
  title: string;
  /** Who the spot is for. Our own spots say JK Automations or Arceus. */
  client: string;
  category: AdCategory;
  layout: AdLayout;
  src: string;
  poster: string;
  durationSec: number;
  /** Plain description for screen readers and the Play button label. */
  alt: string;
  home: boolean;
  library: boolean;
  /** Shown as a small mono label under scenes, so the method is never hidden. */
  madeWith?: string;
  audio?: boolean;
  /** Permalink when the spot is published on @jk_automations; posted spots lead each list. */
  instagram?: string;
}

export const AD_CATEGORIES: readonly { id: AdCategory; label: string; blurb: string }[] = [
  {
    id: 'software',
    label: 'Software ads',
    blurb: 'Product spots for Arceus and JK: screens, type and motion only. No stock footage, no actors.',
  },
  {
    id: 'ai-video',
    label: 'AI video ads',
    blurb: 'Generated commercials for products and concepts, cut and finished in house.',
  },
  {
    id: 'fight',
    label: 'Fight scenes',
    blurb: 'Choreographed fight clips from our own video pipeline, for gyms, studios and promos.',
  },
  {
    id: 'car',
    label: 'Car and chase scenes',
    blurb: 'Driving, racing and chase clips from the same pipeline, for dealers, wraps and detailers.',
  },
];

const LTX = 'LTX-2.5 on our Mac Studio';

export const ADS: readonly Ad[] = [
  /* ---- software ads (home + library); the ones posted on @jk_automations lead ---- */
  {
    id: 'arceus-seo-story',
    title: 'Search story',
    client: 'Arceus',
    category: 'software',
    layout: 'phone',
    src: '/ads/arceus-seo-story.mp4',
    poster: '/ads/arceus-seo-story.webp',
    durationSec: 19,
    alt: 'Search story: a customer searches on a phone, the listing has gaps, Lens prepares four fixes and the owner approves them',
    home: true,
    library: true,
    audio: true,
    instagram: 'https://www.instagram.com/reel/DfA8dJORmUE/',
  },  {
    id: 'jk-everything',
    title: 'Meet JK Automations',
    client: 'JK Automations',
    category: 'software',
    layout: 'phone',
    src: '/ads/jk-everything.mp4',
    poster: '/ads/jk-everything.webp',
    durationSec: 43,
    alt: 'Meet JK Automations: a voice-over walks through marketing agents, custom apps, bank pools and after-hours notifications over clean UI cards',
    home: true,
    library: true,
    audio: true,
    instagram: 'https://www.instagram.com/reel/Denbsg3RhVd/',
  },  {
    id: 'jk-one-message',
    title: 'One message',
    client: 'Arceus',
    category: 'software',
    layout: 'phone',
    src: '/ads/jk-one-message.mp4',
    poster: '/ads/jk-one-message.webp',
    durationSec: 29,
    alt: 'One message: an owner texts Arceus that the shop is closed on Thanksgiving, and each agent returns a finished draft to approve',
    home: true,
    library: true,
    audio: true,
    instagram: 'https://www.instagram.com/reel/DekL0F8RZhi/',
  },  {
    id: 'jk-agent-team',
    title: 'Your team, supercharged',
    client: 'JK Automations',
    category: 'software',
    layout: 'landscape',
    src: '/ads/jk-agent-team.mp4',
    poster: '/ads/jk-agent-team.webp',
    durationSec: 23,
    alt: 'Agent team: people in a chat app mention Arceus agents by name and each one answers with a finished result',
    home: true,
    library: true,
    audio: true,
  },




  {
    id: 'jk-3d-ad',
    title: 'The team at work',
    client: 'JK Automations',
    category: 'software',
    layout: 'phone',
    src: '/reel/jk-3d-ad.mp4',
    poster: '/reel/jk-3d-ad.jpg',
    durationSec: 20,
    alt: 'JK 3D ad: the camera flies over a San Diego map as agents build the campaign, cut the reel and log new leads',
    home: false,
    library: true,
    instagram: 'https://www.instagram.com/reel/DeR7MCkR0cp/',
  },
  {
    id: 'jk-fast-cut',
    title: 'Fast cut',
    client: 'JK Automations',
    category: 'software',
    layout: 'phone',
    src: '/reel/jk-fast-cut.mp4',
    poster: '/reel/jk-fast-cut.jpg',
    durationSec: 10,
    alt: 'JK fast-cut ad: calls, texts, ads, posts, website and the Friday report, then You just approve',
    home: false,
    library: true,
  },

  /* ---- generated scenes (a few on home, more in the library) ---- */
  {
    id: 'f-muaythai',
    title: 'Alley, Muay Thai',
    client: 'Scene study',
    category: 'fight',
    layout: 'scene',
    src: '/ads/f-muaythai.mp4',
    poster: '/ads/f-muaythai.webp',
    durationSec: 6,
    alt: 'Two fighters trade Muay Thai kicks in a rain-soaked alley',
    home: true,
    library: true,
    madeWith: LTX,
  },
  {
    id: 'car-coastal',
    title: 'Coast road, buggies',
    client: 'Scene study',
    category: 'car',
    layout: 'scene',
    src: '/ads/car-coastal.mp4',
    poster: '/ads/car-coastal.webp',
    durationSec: 8,
    alt: 'Two off-road buggies race along a lighthouse coast road at sunset, one drifting in a cloud of dust',
    home: true,
    library: true,
    madeWith: LTX,
  },
  {
    id: 'f-taekwondo',
    title: 'Courtyard, Taekwondo',
    client: 'Scene study',
    category: 'fight',
    layout: 'scene',
    src: '/ads/f-taekwondo.mp4',
    poster: '/ads/f-taekwondo.webp',
    durationSec: 8,
    alt: 'Two martial artists spar in a temple courtyard, a spinning kick met with a block',
    home: true,
    library: true,
    madeWith: LTX,
    instagram: 'https://www.instagram.com/reel/DfH5J6-RLeu/',
  },
  {
    id: 'car-city-night',
    title: 'City, night',
    client: 'Scene study',
    category: 'car',
    layout: 'scene',
    src: '/ads/car-city-night.mp4',
    poster: '/ads/car-city-night.webp',
    durationSec: 8,
    alt: 'Two supercars race through wet night streets under neon-lit towers',
    home: true,
    library: true,
    madeWith: LTX,
  },
  {
    id: 'f-boxing',
    title: 'Ring, boxing',
    client: 'Scene study',
    category: 'fight',
    layout: 'scene',
    src: '/ads/f-boxing.mp4',
    poster: '/ads/f-boxing.webp',
    durationSec: 8,
    alt: 'Two boxers exchange combinations under ring lights',
    home: false,
    library: true,
    madeWith: LTX,
  },
  {
    id: 'f-bjj',
    title: 'Mat, jiu-jitsu',
    client: 'Scene study',
    category: 'fight',
    layout: 'scene',
    src: '/ads/f-bjj.mp4',
    poster: '/ads/f-bjj.webp',
    durationSec: 8,
    alt: 'A Brazilian jiu-jitsu roll: guard pass, sweep and a submission attempt',
    home: false,
    library: true,
    madeWith: LTX,
  },
  {
    id: 'car-mountain',
    title: 'Mountain road',
    client: 'Scene study',
    category: 'car',
    layout: 'scene',
    src: '/ads/car-mountain.mp4',
    poster: '/ads/car-mountain.webp',
    durationSec: 8,
    alt: 'Off-road buggies chase a truck up a misty mountain road, cutting to the driver\'s view',
    home: false,
    library: true,
    madeWith: LTX,
  },
  {
    id: 'car-rally-snow',
    title: 'Rally, snow',
    client: 'Scene study',
    category: 'car',
    layout: 'scene',
    src: '/ads/car-rally-snow.mp4',
    poster: '/ads/car-rally-snow.webp',
    durationSec: 8,
    alt: 'A rally car slides through a snow stage, spraying powder on the corner',
    home: false,
    library: true,
    madeWith: LTX,
    instagram: 'https://www.instagram.com/reel/DfH5SSbRymw/',
  },
  {
    id: 'superbike',
    title: 'Superbike',
    client: 'Scene study',
    category: 'car',
    layout: 'scene',
    src: '/ads/superbike.mp4',
    poster: '/ads/superbike.webp',
    durationSec: 8,
    alt: 'A blue superbike leans through a seaside village corner',
    home: false,
    library: true,
    madeWith: LTX,
  },

  /* ---- AI video ads (library only); posted spots first ---- */
  {
    id: 'tokyo-night',
    title: 'Tokyo after dark',
    client: 'JK Automations',
    category: 'ai-video',
    layout: 'landscape',
    src: '/ads/tokyo-night.mp4',
    poster: '/ads/tokyo-night.webp',
    durationSec: 20,
    alt: 'Tokyo night run: a sports car through neon Tokyo streets, then Mount Fuji at first light',
    home: false,
    library: true,
    audio: true,
    instagram: 'https://www.instagram.com/reel/Defj6wSRFiI/',
  },
  {
    id: 'svj-glacier',
    title: 'Glacier run',
    client: 'JK Automations',
    category: 'ai-video',
    layout: 'phone',
    src: '/ads/svj-glacier.mp4',
    poster: '/ads/svj-glacier.webp',
    durationSec: 16,
    alt: 'Glacier run: a supercar on an ice road, when your business gets stuck build a better way forward',
    home: false,
    library: true,
    audio: true,
    instagram: 'https://www.instagram.com/reel/DeLjtbcRyHQ/',
  },
  {
    id: 'rs6-phone',
    title: 'Piecing it together',
    client: 'JK Automations',
    category: 'ai-video',
    layout: 'phone',
    src: '/ads/rs6-phone.mp4',
    poster: '/ads/rs6-phone.webp',
    durationSec: 21,
    alt: 'Phone post: a fast wagon on an open road with the voice-over, tired of piecing your business together',
    home: false,
    library: true,
    audio: true,
    instagram: 'https://www.instagram.com/reel/DeqDJNYRAFU/',
  },
  {
    id: 'hoverboard-v2',
    title: 'NYC hoverboard',
    client: 'JK Automations',
    category: 'ai-video',
    layout: 'phone',
    src: '/ads/hoverboard-v2.mp4',
    poster: '/ads/hoverboard-v2.webp',
    durationSec: 29,
    alt: 'Hoverboard spot: a rider weaves through New York traffic to make a sales meeting as a new lead comes in',
    home: false,
    library: true,
    audio: true,
  },
  {
    id: 'ironload-v3',
    title: 'IronLoad',
    client: 'IronLoad',
    category: 'ai-video',
    layout: 'phone',
    src: '/ads/ironload-v3.mp4',
    poster: '/ads/ironload-v3.webp',
    durationSec: 18,
    alt: 'IronLoad: a cinematic spot for the load-profit calculator, a truck route and the estimated profit',
    home: false,
    library: true,
    audio: true,
  },
  {
    id: 'lifeos-3d',
    title: 'LifeOS',
    client: 'LifeOS',
    category: 'ai-video',
    layout: 'phone',
    src: '/ads/lifeos-3d.mp4',
    poster: '/ads/lifeos-3d.webp',
    durationSec: 10,
    alt: 'LifeOS: a 3D product spot for the personal operating system app',
    home: false,
    library: true,
    audio: true,
  },
  {
    id: 'the-climb',
    title: 'The Climb',
    client: 'JK Automations',
    category: 'ai-video',
    layout: 'phone',
    src: '/reel/the-climb.mp4',
    poster: '/reel/the-climb.jpg',
    durationSec: 20,
    alt: 'The Climb: painted alpine peaks at sunrise as two climbers reach the summit',
    home: false,
    library: true,
    instagram: 'https://www.instagram.com/reel/DeQnz1Fx-e7/',
  },
  {
    id: 'escalade',
    title: 'Coast road spot',
    client: 'Escalade',
    category: 'ai-video',
    layout: 'phone',
    src: '/reel/escalade.mp4',
    poster: '/reel/escalade.jpg',
    durationSec: 15,
    alt: 'Escalade spot: a black SUV on a coastal highway at golden hour, then inside the cabin',
    home: false,
    library: true,
    instagram: 'https://www.instagram.com/reel/DeODNVfx6kf/',
  },
  {
    id: 'matcha-beach',
    title: 'Beach menu spot',
    client: 'Matcha café',
    category: 'ai-video',
    layout: 'phone',
    src: '/reel/matcha-beach.mp4',
    poster: '/reel/matcha-beach.jpg',
    durationSec: 15,
    alt: 'Matcha café menu spot: each drink on the sand at sunset with its name',
    home: false,
    library: true,
  },
];

/* ---- Reel cards: the shape components/Reel.tsx renders ---- */

export interface ReelCard {
  id: string;
  kind: 'video' | 'still' | 'end';
  /** default 'phone' (9:16); 'landscape' is 16:9; 'scene' is the 768x448 LTX frame */
  layout?: AdLayout;
  src?: string;
  poster?: string;
  alt?: string;
  caption?: string;
  href?: string;
}

export const toReelCard = (a: Ad): ReelCard => ({
  id: a.id,
  kind: 'video',
  layout: a.layout,
  src: a.src,
  poster: a.poster,
  alt: a.alt,
  caption: a.madeWith ? `${a.title} · ${a.madeWith}` : `${a.client} · ${a.title}`,
});

const END_CARD: ReelCard = { id: 'your-ad-here', kind: 'end', href: '/contact' };

/** Home, first reel: the software ads, strongest first, then the "yours" card. */
export const HOME_SOFTWARE_REEL: readonly ReelCard[] = [
  ...ADS.filter((a) => a.home && a.category === 'software').map(toReelCard),
  END_CARD,
];

/** Home, second reel: a few generated scenes. */
export const HOME_SCENES_REEL: readonly ReelCard[] = ADS.filter(
  (a) => a.home && (a.category === 'fight' || a.category === 'car')
).map(toReelCard);

export const libraryAds = (category: AdCategory): readonly Ad[] =>
  ADS.filter((a) => a.library && a.category === category);

export const adById = (id: string): Ad | undefined => ADS.find((a) => a.id === id);

export const formatDuration = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};
